import { LoadingOverlay, Button } from '@finpay-development/shared-components';
import { Box, Paper } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { base64ToBlob } from '@finpay-development/shared-platform/lib/esm/shared/utils/pdf-utils';
import { showErrorStatus } from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import { AppDispatch } from '../../../../shared/state/store';
import { PdfView } from '../../../../shared/components/pdf-view';
import { estimateService } from '../../../services/estimate-service';
import {
  DEFAULT_PREVIEW_SECTIONS,
  PreviewSections,
  SectionSelectionModal,
} from './section-selection-modal';

export const EstimatePreviewSummary = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { eid } = useParams<{ eid?: string }>();

  const [isDataLoading, setIsDataLoading] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState<PreviewSections>(
    DEFAULT_PREVIEW_SECTIONS
  );

  const handlePrintPdf = () => {
    if (!pdfBlob) return;

    const url = URL.createObjectURL(pdfBlob);
    const printWindow = window.open(url, '_blank');
    if (!printWindow) {
      URL.revokeObjectURL(url);
      dispatch(showErrorStatus('Unable to open PDF for printing.'));
      return;
    }

    // Safari often fires load before the PDF viewer has painted; delay print
    // so the print preview isn't an empty/tiny snapshot.
    printWindow.addEventListener(
      'load',
      () => {
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 500);
      },
      { once: true }
    );

    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  useEffect(() => {
    const estimateId = Number(eid);
    if (!eid || Number.isNaN(estimateId)) {
      if (eid) {
        dispatch(showErrorStatus('Invalid estimate id'));
      }
      return;
    }

    let isActive = true;

    const fetchPreviewPdf = async () => {
      setIsDataLoading(true);
      setPdfBlob(null);

      try {
        const response = await estimateService.createEstimateAuthDocument(
          estimateId,
          { sections: visibleSections }
        );

        if (!isActive) return;

        if (response.hasErrors) {
          dispatch(
            showErrorStatus(
              response.entity?.message ||
                response.errorMessage ||
                'Failed to load estimate preview'
            )
          );
          return;
        }

        const contentBase64 = response.entity?.data?.contentBase64;
        const contentType =
          response.entity?.data?.contentType || 'application/pdf';

        if (!contentBase64 || typeof contentBase64 !== 'string') {
          dispatch(showErrorStatus('Failed to load estimate preview'));
          return;
        }

        setPdfBlob(base64ToBlob(contentBase64, contentType));
      } catch (error) {
        if (!isActive) return;
        console.error(error);
        dispatch(showErrorStatus('Failed to load estimate preview'));
      } finally {
        if (isActive) {
          setIsDataLoading(false);
        }
      }
    };

    fetchPreviewPdf();

    return () => {
      isActive = false;
    };
  }, [eid, dispatch, visibleSections]);

  return (
    <Box>
      {isDataLoading ? (
        <LoadingOverlay />
      ) : (
        <Box
          className="patient-details"
          component="main"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            px: 2,
            pt: 2,
            pb: 4,
          }}
        >
          <Paper
            sx={{
              width: 850,
              maxWidth: '100%',
              height: '70vh',
              padding: 0,
              overflow: 'hidden',
            }}
            test-id="estimate-preview-summary-container"
          >
            <div style={{ height: '100%' }}>
              {pdfBlob ? (
                <PdfView
                  pdfBlob={pdfBlob}
                  test-id="estimate-preview-summary-pdf"
                  onError={() => {
                    dispatch(showErrorStatus('Unable to display estimate preview PDF'));
                    setPdfBlob(null);
                  }}
                />
              ) : null}
            </div>
          </Paper>
          <Box
            mt={2}
            display="flex"
            justifyContent="flex-end"
            gap={2}
            width={850}
            maxWidth="100%"
          >
            <Button
              test-id="estimate-preview-select-sections"
              onClick={() => setIsModalOpen(true)}
            >
              Select Sections
            </Button>
            <Button
              test-id="print-estimate-preview-summary"
              onClick={handlePrintPdf}
              disabled={!pdfBlob}
            >
              Print / Save as PDF
            </Button>
          </Box>
        </Box>
      )}
      {isModalOpen && (
        <SectionSelectionModal
          setIsModalOpen={setIsModalOpen}
          initialVisibleSections={visibleSections}
          onUpdateVisibleSections={setVisibleSections}
        />
      )}
    </Box>
  );
};
